import { __ } from "@wordpress/i18n";
import { useState } from "@wordpress/element";
import { api } from "@/config/api-fetch";
import { toast } from "react-toastify";
import { Section } from "@/components/section";
import { css } from "@/utils/css";
import { Select } from "@/components/select";
import { PasswordInput } from "@/components/password-input";
import { OPENAI, GEMINI, LAST_ACTIVE_PROVIDER, IS_PRO } from "@/utils/constants";
import { useProModal } from "@/components/pro-alert-box";

type ModelOption = {
    value: string;
    label: string;
};

// Helper to ensure the selected model is always in the options list
const ensureSelectedModelInList = (models: ModelOption[], selectedValue: string): ModelOption[] => {
    if (!selectedValue) {
        return models;
    }
    const exists = models.some((m) => m.value === selectedValue);
    if (!exists) {
        // Add the selected model at the beginning with a formatted label
        return [{ value: selectedValue, label: selectedValue }, ...models];
    }
    return models;
};

export default function Antimanual() {
    // Provider state
    const [provider, setProvider] = useState<"openai" | "gemini">(LAST_ACTIVE_PROVIDER);

    // OpenAI State - API key starts empty, user must enter new key to update
    const [openAiApiKey, setOpenAiApiKey] = useState("");
    const [openAiModel, setOpenAiModel] = useState(OPENAI.MODEL);
    const [openAiEmbeddingModel, setOpenAiEmbeddingModel] = useState(OPENAI.EMBEDDING_MODEL);

    // Gemini State - API key starts empty, user must enter new key to update
    const [geminiApiKey, setGeminiApiKey] = useState("");
    const [geminiModel, setGeminiModel] = useState(GEMINI.MODEL);
    const [geminiEmbeddingModel, setGeminiEmbeddingModel] = useState(GEMINI.EMBEDDING_MODEL);

    const [saving, setSaving] = useState(false);
    const { showProAlert } = useProModal();

    // Get model options from constants (provided by server)
    const getOpenAiChatModels = (): ModelOption[] => {
        return ensureSelectedModelInList(OPENAI.CHAT_MODELS as ModelOption[], openAiModel);
    };

    const getOpenAiEmbeddingModels = (): ModelOption[] => {
        return ensureSelectedModelInList(OPENAI.EMBEDDING_MODELS as ModelOption[], openAiEmbeddingModel);
    };

    const getGeminiChatModels = (): ModelOption[] => {
        return ensureSelectedModelInList(GEMINI.CHAT_MODELS as ModelOption[], geminiModel);
    };

    const getGeminiEmbeddingModels = (): ModelOption[] => {
        return ensureSelectedModelInList(GEMINI.EMBEDDING_MODELS as ModelOption[], geminiEmbeddingModel);
    };

    const handleProviderChange = (newProvider: "openai" | "gemini") => {
        // Check if user is trying to select Gemini without pro
        if (newProvider === "gemini" && !IS_PRO) {
            showProAlert(
                __("Gemini is a Premium Feature", "antimanual"),
                __(
                    "Google Gemini AI is available exclusively for Pro users. Upgrade to Antimanual Pro to unlock Gemini and access all premium features.",
                    "antimanual",
                ),
            );
            return;
        }
        setProvider(newProvider);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        const isGemini = provider === "gemini";
        const endpoint = isGemini ? "/preferences/gemini" : "/preferences/openai";

        // Only include api_key if user entered a new one
        const currentApiKey = isGemini ? geminiApiKey : openAiApiKey;
        const hasExistingKey = isGemini ? GEMINI.HAS_KEY : OPENAI.HAS_KEY;

        // If no existing key and no new key entered, show error
        if (!hasExistingKey && !currentApiKey.trim()) {
            toast.error(__("Please enter an API key.", "antimanual"));
            setSaving(false);
            return;
        }

        const config: Record<string, string> = isGemini
            ? {
                  response_model: geminiModel,
                  embedding_model: geminiEmbeddingModel,
              }
            : {
                  response_model: openAiModel,
                  embedding_model: openAiEmbeddingModel,
              };

        // Only send api_key if user entered a new one
        if (currentApiKey.trim()) {
            config.api_key = currentApiKey;
        }

        try {
            await api.post(endpoint, config);
            toast.success(__("Settings saved successfully!", "antimanual"));
            // Clear the input after successful save (key is now stored encrypted)
            if (isGemini) {
                setGeminiApiKey("");
            } else {
                setOpenAiApiKey("");
            }
        } catch (error: any) {
            const errorMessage = error?.message || __("An error occurred while saving settings.", "antimanual");
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    // Helper to render API key status indicator
    const renderKeyStatus = (hasKey: boolean, maskedKey: string) => {
        if (!hasKey) return null;
        return (
            <div className={css("api-key-status")}>
                <span className={css("api-key-status__indicator api-key-status__indicator--configured")}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                        <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {__("Key Configured", "antimanual")}
                </span>
                {maskedKey && <code className={css("api-key-status__masked")}>{maskedKey}</code>}
            </div>
        );
    };

    return (
        <Section>
            <div id="antimanual" className={'tab-content antimanual-tab-pane ' + css("px-6")}>
                <h2>{__("AI Provider Configuration", "antimanual")}</h2>
                <p className="description">{__("Configure your AI provider settings to enable the AI features of Antimanual.", "antimanual")}</p>

                <form onSubmit={handleSubmit}>
                    <table className="form-table">
                        <tbody>
                            <tr>
                                <th scope="row">
                                    <label htmlFor="ai_provider">{__("Select AI Provider:", "antimanual")}</label>
                                </th>
                                <td>
                                    <Select
                                        className={css("form-input form-select max-w-md")}
                                        value={provider}
                                        onChange={(e) => handleProviderChange(e as any)}
                                        options={[
                                            { value: "openai", label: "OpenAI" },
                                            {
                                                value: "gemini",
                                                label: IS_PRO ? "Google Gemini" : "Google Gemini",
                                            },
                                        ]}
                                        help={__("Choose which AI provider to use for chat and embeddings.", "antimanual")}
                                    />
                                </td>
                            </tr>

                            {provider === "openai" ? (
                                <>
                                    <tr>
                                        <th scope="row">
                                            <label htmlFor="openai_api_key">{__("OpenAI API Key:", "antimanual")}</label>
                                        </th>
                                        <td>
                                            {renderKeyStatus(OPENAI.HAS_KEY, OPENAI.MASKED_KEY)}
                                            <PasswordInput
                                                className="max-w-md"
                                                name="openai_api_key"
                                                value={openAiApiKey}
                                                onChange={setOpenAiApiKey}
                                                placeholder={
                                                    OPENAI.HAS_KEY
                                                        ? __("Enter new key to replace", "antimanual")
                                                        : __("Enter your API key", "antimanual")
                                                }
                                            />
                                            {OPENAI.HAS_KEY && (
                                                <p className="description">
                                                    {__("Leave empty to keep existing key, or enter a new key to replace it.", "antimanual")}
                                                </p>
                                            )}
                                            <div className={css("api-key-url-callout max-w-md")}>
                                                <div className={css("api-key-url-callout__content")}>
                                                    <div className={css("api-key-url-callout__icon")}>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                            width="16"
                                                            height="16"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M8 7a5 5 0 113.61 4.804l-1.903 1.903A1 1 0 019 14H8v1a1 1 0 01-1 1H6v1a1 1 0 01-1 1H3a1 1 0 01-1-1v-2a1 1 0 01.293-.707L8.196 8.39A5.002 5.002 0 018 7zm5-3a.75.75 0 000 1.5A1.5 1.5 0 0114.5 7 .75.75 0 0016 7a3 3 0 00-3-3z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div className={css("api-key-url-callout__text")}>
                                                        <strong>{__("Need an OpenAI API key?", "antimanual")}</strong>
                                                        <span>{__("Get it from your OpenAI dashboard", "antimanual")}</span>
                                                    </div>
                                                </div>
                                                <a
                                                    href="https://platform.openai.com/account/api-keys"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={css("api-key-url-callout__link")}
                                                >
                                                    {__("Get API Key", "antimanual")}
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                        width="14"
                                                        height="14"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope="row">
                                            <label htmlFor="openai_model">{__("OpenAI Model:", "antimanual")}</label>
                                        </th>
                                        <td>
                                            <Select
                                                className={css("form-input form-select max-w-md")}
                                                value={openAiModel}
                                                onChange={(e) => setOpenAiModel(e)}
                                                options={getOpenAiChatModels()}
                                                help={__("Select the chat model to use for AI responses.", "antimanual")}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope="row">
                                            <label htmlFor="openai_embedding_model">{__("OpenAI Embedding Model:", "antimanual")}</label>
                                        </th>
                                        <td>
                                            <Select
                                                className={css("form-input form-select max-w-md")}
                                                value={openAiEmbeddingModel}
                                                onChange={(e) => setOpenAiEmbeddingModel(e)}
                                                options={getOpenAiEmbeddingModels()}
                                                help={__("Select the embedding model to use for knowledge base.", "antimanual")}
                                            />
                                        </td>
                                    </tr>
                                </>
                            ) : (
                                <>
                                    <tr>
                                        <th scope="row">
                                            <label htmlFor="gemini_api_key">{__("Gemini API Key:", "antimanual")}</label>
                                        </th>
                                        <td>
                                            {renderKeyStatus(GEMINI.HAS_KEY, GEMINI.MASKED_KEY)}
                                            <PasswordInput
                                                className="max-w-md"
                                                name="gemini_api_key"
                                                value={geminiApiKey}
                                                onChange={setGeminiApiKey}
                                                placeholder={
                                                    GEMINI.HAS_KEY
                                                        ? __("Enter new key to replace", "antimanual")
                                                        : __("Enter your API key", "antimanual")
                                                }
                                            />
                                            {GEMINI.HAS_KEY && (
                                                <p className="description">
                                                    {__("Leave empty to keep existing key, or enter a new key to replace it.", "antimanual")}
                                                </p>
                                            )}
                                            <div className={css("api-key-url-callout max-w-md")}>
                                                <div className={css("api-key-url-callout__content")}>
                                                    <div className={css("api-key-url-callout__icon")}>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                            width="16"
                                                            height="16"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M8 7a5 5 0 113.61 4.804l-1.903 1.903A1 1 0 019 14H8v1a1 1 0 01-1 1H6v1a1 1 0 01-1 1H3a1 1 0 01-1-1v-2a1 1 0 01.293-.707L8.196 8.39A5.002 5.002 0 018 7zm5-3a.75.75 0 000 1.5A1.5 1.5 0 0114.5 7 .75.75 0 0016 7a3 3 0 00-3-3z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div className={css("api-key-url-callout__text")}>
                                                        <strong>{__("Need a Gemini API key?", "antimanual")}</strong>
                                                        <span>{__("Get it from Google AI Studio", "antimanual")}</span>
                                                    </div>
                                                </div>
                                                <a
                                                    href="https://aistudio.google.com/app/apikey"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={css("api-key-url-callout__link")}
                                                >
                                                    {__("Get API Key", "antimanual")}
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                        width="14"
                                                        height="14"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope="row">
                                            <label htmlFor="gemini_model">{__("Gemini Model:", "antimanual")}</label>
                                        </th>
                                        <td>
                                            <Select
                                                className={css("form-input form-select max-w-md")}
                                                value={geminiModel}
                                                onChange={(e) => setGeminiModel(e)}
                                                options={getGeminiChatModels()}
                                                help={__("Select the chat model to use for AI responses.", "antimanual")}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope="row">
                                            <label htmlFor="gemini_embedding_model">{__("Gemini Embedding Model:", "antimanual")}</label>
                                        </th>
                                        <td>
                                            <Select
                                                className={css("form-input form-select max-w-md")}
                                                value={geminiEmbeddingModel}
                                                onChange={(e) => setGeminiEmbeddingModel(e)}
                                                options={getGeminiEmbeddingModels()}
                                                help={__("Select the embedding model to use for knowledge base.", "antimanual")}
                                            />
                                        </td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                    <p className="submit">
                        <button type="submit" className={css("btn btn-primary")} disabled={saving}>
                            {saving ? __("Saving...", "antimanual") : __("Save Settings", "antimanual")}
                        </button>
                    </p>
                </form>
            </div>
        </Section>
    );
}
